/**
 * The play Clu draws on the canvas while it writes it (the stream's
 * `play_draft`): parsed from the stream, kept per chat and turn in the store
 * (`agentPlayDrafts`), handed over to the proposal the turn stores, and the
 * moment that proposal shows, for the turn's `clu_turn_progress`.
 */
import { asRecord } from '@/components/agent-chat/as-record.util';
import {
  clearAgentPlayDraft,
  markDraftHandedOver,
  setAgentPlayDraft,
  type AgentPlayDraft,
} from '@/store/features/ui.feature';
import type { CustomGraph } from '@/types/custom-playbooks';

/** What the composer field reads while Clu writes a play. */
export type TurnProgress = {
  label: string;
  step: number | null;
  of: number | null;
};

/** The tool whose result a play draft becomes. */
const PROPOSE_PLAY_CHANGES = 'propose_play_changes';

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

function count(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function ids(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((id): id is string => typeof id === 'string' && !!id)
    : [];
}

/**
 * A `play_draft` event's draft and progress; null when it names no turn. A
 * one-off run's names no agent: its progress shows, with no canvas draft.
 */
export function playDraftOf(
  data: Record<string, unknown>
): { draft: AgentPlayDraft | null; progress: TurnProgress } | null {
  const conversationId = text(data.conversationId);
  const turnId = text(data.turnId);
  const agentId = text(data.agentId);
  const playTarget = text(data.playTarget);
  const label = text(data.label);
  if (!conversationId || !turnId || !label) return null;
  const progress = { label, step: count(data.step), of: count(data.of) };
  if (!agentId || !playTarget) return { draft: null, progress };
  const graph = asRecord(data.graph);
  return {
    draft: {
      key: `${conversationId}:${turnId}`,
      agentId,
      playTarget,
      conversationId,
      attempt: count(data.attempt) ?? 0,
      label,
      graph:
        graph && Array.isArray(graph.nodes)
          ? (graph as unknown as CustomGraph)
          : null,
      added: ids(data.added),
      changed: ids(data.changed),
    },
    progress,
  };
}

type Dispatch = (
  action: ReturnType<
    | typeof setAgentPlayDraft
    | typeof markDraftHandedOver
    | typeof clearAgentPlayDraft
  >
) => unknown;

/**
 * One turn's play draft as its stream goes: each `play_draft` is stored; the
 * turn's proposal result hands it over to the proposal it stored, and an
 * error from propose_play_changes clears it, one that stored a blocked
 * candidate too; the turn's end clears one never handed over. `end` returns
 * the handed-over draft's key, and can be called again.
 */
export function turnPlayDraft(dispatch: Dispatch) {
  let key: string | null = null;
  let attempt = 0;
  let handedOver = false;
  let ended = false;
  /** A one-off's progress shows, with no draft, until a tool's result. */
  let progressOnly = false;
  return {
    /** The progress the composer shows, or null for an unreadable event or
     *  one late for a draft already handed over (only a newer attempt, after
     *  a refusal, writes again). */
    draft(data: Record<string, unknown>): TurnProgress | null {
      const parsed = ended ? null : playDraftOf(data);
      if (!parsed) return null;
      if (!parsed.draft) {
        progressOnly = true;
        return parsed.progress;
      }
      if (handedOver && parsed.draft.attempt <= attempt) return null;
      key = parsed.draft.key;
      attempt = parsed.draft.attempt;
      handedOver = false;
      dispatch(setAgentPlayDraft(parsed.draft));
      return parsed.progress;
    },
    /** True when the result ends the draft (the composer's progress goes). */
    result(tool: { name?: unknown; result?: unknown; display?: unknown }) {
      if (progressOnly && !key && !ended) {
        progressOnly = false;
        return true;
      }
      if (!key || handedOver || ended) return false;
      const result = asRecord(tool.result);
      // A refusal clears it first: one that stored a blocked candidate
      // carries its play_proposal display and proposal id too.
      if (tool.name === PROPOSE_PLAY_CHANGES && result?.error) {
        dispatch(clearAgentPlayDraft(key));
        key = null;
        return true;
      }
      const proposalId = text(result?.proposal_id);
      if (asRecord(tool.display)?.type === 'play_proposal' && proposalId) {
        handedOver = true;
        dispatch(markDraftHandedOver({ key, proposalId, at: Date.now() }));
        return true;
      }
      return false;
    },
    end(): string | null {
      if (!ended && key && !handedOver) dispatch(clearAgentPlayDraft(key));
      ended = true;
      return handedOver ? key : null;
    },
  };
}

/** When each handed-over draft's proposal showed, until its turn reads it. */
const renders = new Map<string, number>();
const waiting = new Map<string, (at: number) => void>();
/** Renders kept for turns that have not read theirs (one a turn). */
const KEPT_RENDERS = 20;

/** The proposal draft `key` was handed over to shows on the canvas now. */
export function proposalRendered(key: string, at: number = Date.now()) {
  const waiter = waiting.get(key);
  if (waiter) return waiter(at);
  renders.delete(key);
  renders.set(key, at);
  if (renders.size > KEPT_RENDERS)
    renders.delete(renders.keys().next().value as string);
}

/**
 * When draft `key`'s proposal showed, waiting up to `ms` for it: null when
 * it did not show in that time (the Play tab was not open on it).
 */
export function whenProposalRendered(
  key: string,
  ms: number
): Promise<number | null> {
  const at = renders.get(key);
  if (at !== undefined) {
    renders.delete(key);
    return Promise.resolve(at);
  }
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      waiting.delete(key);
      resolve(null);
    }, ms);
    waiting.set(key, (shown) => {
      clearTimeout(timer);
      waiting.delete(key);
      resolve(shown);
    });
  });
}
